import {
  Button,
  Content,
  FillIcon,
  Flex,
  FormInput,
  fromTheme,
  Grid,
  Hex,
  Option,
  RadioGroupBox,
  StrokeIcon,
  TileRadioWrapper,
  Tooltip,
  useBaseModal,
  usePlatformProps,
} from '@acsbe/ui';
import { wcagContrastChecker } from '@mdhnpm/wcag-contrast-checker';
import { PropsWithChildren, useMemo } from 'react';
import { useFieldArray } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import languages from '../../../assets/locales/app/languages.json';
import { FormComboBox } from '../../../ui/FormCombobox';
import { LearnFeatureModal } from '../../../ui/LearnFeaturePopup';
import { CustomInstallationConfig } from '../../../validations';
import { FormSection } from './FormSection';
import { useInstallationForm } from './hooks';

export type InstallationFormProps = PropsWithChildren<{
  form: ReturnType<typeof useInstallationForm>;
  onSubmit?: (formData: CustomInstallationConfig) => void;
  notifyOnChange?: (formData: CustomInstallationConfig) => void;
}>;

const INSTALLATION_FORM_PAGE_KEY = 'INSTALLATION_PAGE.ADVANCED_INSTALL';
const MAX_CONSISTENT_HELP_INPUTS = 14;

const HELP_TYPE_PLACEHOLDER_MAP: Record<string, string> = {
  'Support Page': 'SUPPORT_PLACEHOLDER',
  'FAQ Page': 'FAQ_PLACEHOLDER',
  'Contact Email': 'EMAIL_PLACEHOLDER',
  'Contact Phone': 'PHONE_PLACEHOLDER',
  'Operation Hours': 'OPERATIONS_PLACEHOLDER',
  'Contact Page': 'CONTACT_PLACEHOLDER',
  'Chat Support': 'CHAT_PLACEHOLDER',
};

export function InstallationForm({
  form,
  onSubmit,
  notifyOnChange,
  children,
}: InstallationFormProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const p = usePlatformProps();
  const inputWidth = theme.size.inputWidth;

  const leadColor = form.watch('leadColor');
  const triggerColor = form.watch('triggerColor');

  const { triggerBaseModal: triggerLearnFeatureModal } =
    useBaseModal(LearnFeatureModal);
  const {
    fields: consistentHelpData,
    append: consistentHelpDataAppend,
    remove: consistentHelpDataRemove,
  } = useFieldArray({
    control: form.control,
    name: 'consistentHelpData',
  });

  const colorAccessibleContrast = (
    <Content inline scale="alternative">
      <Content inline scale="danger" emphasis="low">
        {t('IMPORTANT')}:&nbsp;
      </Content>
      {t(`${INSTALLATION_FORM_PAGE_KEY}.INSUFFICIENT_CONTRAST`)}
    </Content>
  );

  const onDropdownChange = (value: string, eventType: string) => {
    if (notifyOnChange && eventType === 'change')
      form.handleSubmit(notifyOnChange).call(value);
  };

  const isLeadColorAccessible = useHasContrast(leadColor as Hex, {
    r: 255,
    g: 255,
    b: 255,
  });

  const isTriggerColorAccessible = useHasContrast(triggerColor as Hex, {
    r: 255,
    g: 255,
    b: 255,
  });

  const addConsistentHelpField = () => {
    if (consistentHelpData.length < MAX_CONSISTENT_HELP_INPUTS) {
      consistentHelpDataAppend({ title: '', description: '' });
      if (notifyOnChange) {
        form.handleSubmit(notifyOnChange)();
      }
    }
  };

  const removeConsistentHelpField = (idx: number) => {
    if (consistentHelpData.length > 1) {
      consistentHelpDataRemove(idx);
      if (notifyOnChange) {
        form.handleSubmit(notifyOnChange)();
      }
    }
  };

  const handleNumericInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.target.value = event.target.value.replace(/[^0-9]/g, '');
  };

  // const customInstallation =
  //   trpc.installation.customizeInstallationScript.useMutation();

  return (
    <form
      noValidate
      onSubmit={form.handleSubmit(onSubmit!)}
      // aria-busy={customInstallation.isLoading}
      onChange={(e: React.FormEvent<HTMLFormElement>) => {
        if (notifyOnChange) {
          form.handleSubmit(notifyOnChange).call(e);
        }
      }}
    >
      <Flex gap={6}>
        <FormSection
          label={t(`${INSTALLATION_FORM_PAGE_KEY}.INTERFACE_CUSTOMIZATIONS`)}
          {...p.fromDesktopLg({
            sublabel: t(
              `${INSTALLATION_FORM_PAGE_KEY}.INTERFACE_CUSTOMIZATIONS_SUBTITLE`,
            ),
          })}
        >
          <Grid
            cols={`repeat(2, minmax(auto, ${inputWidth}))`}
            gap={6}
            align="baseline"
            justify="space-between"
            {...p.uptoPhablet({ cols: '1fr' })}
          >
            <FormInput
              type="color"
              description={
                isLeadColorAccessible ? undefined : colorAccessibleContrast
              }
              {...form.hookup(
                'leadColor',
                `${INSTALLATION_FORM_PAGE_KEY}.FORM.`,
              )}
            />
            <FormInput
              type="text"
              placeholder={
                t(`${INSTALLATION_FORM_PAGE_KEY}.FORM.BLANK_DEFAULT`)!
              }
              {...form.hookup(
                'statementLink',
                `${INSTALLATION_FORM_PAGE_KEY}.FORM.`,
              )}
            />
            <FormInput
              type="text"
              placeholder={
                t(`${INSTALLATION_FORM_PAGE_KEY}.FORM.BLANK_DEFAULT`)!
              }
              {...form.hookup(
                'footerHtml',
                `${INSTALLATION_FORM_PAGE_KEY}.FORM.`,
              )}
            />

            <FormComboBox
              control={form.control}
              autocomplete
              placeholder={`${INSTALLATION_FORM_PAGE_KEY}.FORM.CHOOSE_LANGUAGE`}
              name="language"
              label={`${INSTALLATION_FORM_PAGE_KEY}.FORM.language`}
              onEvent={onDropdownChange}
            >
              {getLanguagesOptions(t)}
            </FormComboBox>

            <FormComboBox
              control={form.control}
              name="position"
              label={`${INSTALLATION_FORM_PAGE_KEY}.FORM.position`}
              onEvent={onDropdownChange}
            >
              {getXPositionOptions(t)}
            </FormComboBox>
          </Grid>
        </FormSection>

        <FormSection
          label={t(`${INSTALLATION_FORM_PAGE_KEY}.TRIGGER_CUSTOMIZATIONS`)}
          {...p.fromDesktopLg({
            sublabel: t(
              `${INSTALLATION_FORM_PAGE_KEY}.TRIGGER_CUSTOMIZATIONS_SUBTITLE`,
            ),
          })}
        >
          <Grid
            cols={`repeat(2, minmax(auto, ${inputWidth}))`}
            gap={6}
            paddingBlockEnd={6}
            align="baseline"
            justify="space-between"
            {...p.uptoPhablet({ cols: '1fr' })}
          >
            <FormInput
              type="color"
              description={
                isTriggerColorAccessible ? undefined : colorAccessibleContrast
              }
              {...form.hookup(
                'triggerColor',
                `${INSTALLATION_FORM_PAGE_KEY}.FORM.`,
              )}
            />
            <FormComboBox
              control={form.control}
              name="triggerPositionX"
              label={`${INSTALLATION_FORM_PAGE_KEY}.FORM.triggerPositionX`}
              onEvent={onDropdownChange}
            >
              {getXPositionOptions(t)}
            </FormComboBox>

            <FormComboBox
              control={form.control}
              name="triggerPositionY"
              label={`${INSTALLATION_FORM_PAGE_KEY}.FORM.triggerPositionY`}
              onEvent={onDropdownChange}
            >
              {getYPositionOptions(t)}
            </FormComboBox>

            <FormComboBox
              control={form.control}
              name="triggerSize"
              label={`${INSTALLATION_FORM_PAGE_KEY}.FORM.triggerSize`}
              onEvent={onDropdownChange}
            >
              {getSizeOptions(t)}
            </FormComboBox>

            <FormComboBox
              control={form.control}
              name="triggerRadius"
              label={`${INSTALLATION_FORM_PAGE_KEY}.FORM.triggerRadius`}
              onEvent={onDropdownChange}
            >
              {getShapeOptions(t)}
            </FormComboBox>

            <FormComboBox
              control={form.control}
              name="hideTrigger"
              label={`${INSTALLATION_FORM_PAGE_KEY}.FORM.hideTrigger`}
              onEvent={onDropdownChange}
            >
              {getHideElementOptions(t)}
            </FormComboBox>

            <FormInput
              type="number"
              onInput={handleNumericInput}
              {...form.hookup(
                'triggerOffsetX',
                `${INSTALLATION_FORM_PAGE_KEY}.FORM.`,
              )}
            />
            <FormInput
              type="number"
              onInput={handleNumericInput}
              {...form.hookup(
                'triggerOffsetY',
                `${INSTALLATION_FORM_PAGE_KEY}.FORM.`,
              )}
            />
          </Grid>
          <RadioGroupBox
            label={t(`${INSTALLATION_FORM_PAGE_KEY}.FORM.triggerIcon`)}
          >
            <WidgetsIcons
              row
              wrap="wrap"
              rowGap={5}
              colGap={12}
              {...p.uptoDesktopSm({ justify: 'center' })}
            >
              {(
                [
                  'LegacyDisplay',
                  'LegacyDisplay2',
                  'LegacyDisplay3',
                  'LegacyHelp',
                  'LegacyPeople',
                  'LegacyPeople2',
                  'LegacySettings',
                  'LegacySettings2',
                  'LegacyWheels',
                  'LegacyWheels2',
                ] as const
              ).map((name) => (
                <TileRadioWrapper key={name}>
                  <input
                    type="radio"
                    value={name}
                    {...form.register('triggerIcon')}
                  />
                  <FillIcon
                    name={name}
                    size="xl"
                    aria-label={
                      t(`${INSTALLATION_FORM_PAGE_KEY}.ICON_OF`, {
                        name,
                      })!
                    }
                  />
                </TileRadioWrapper>
              ))}
            </WidgetsIcons>
          </RadioGroupBox>
        </FormSection>

        <FormSection
          label={t(`${INSTALLATION_FORM_PAGE_KEY}.MOBILE_CUSTOMIZATIONS`)}
          {...p.fromDesktopLg({
            sublabel: t(
              `${INSTALLATION_FORM_PAGE_KEY}.MOBILE_CUSTOMIZATIONS_SUBTITLE`,
            ),
          })}
        >
          <Grid
            cols={`repeat(2, minmax(auto, ${inputWidth}))`}
            gap={6}
            align="baseline"
            justify="space-between"
            {...p.uptoPhablet({ cols: '1fr' })}
          >
            <FormComboBox
              control={form.control}
              name="hideMobile"
              label={`${INSTALLATION_FORM_PAGE_KEY}.FORM.hideMobile`}
              onEvent={onDropdownChange}
            >
              {getHideElementOptions(t)}
            </FormComboBox>

            <FormComboBox
              control={form.control}
              name="mobileTriggerPositionX"
              label={`${INSTALLATION_FORM_PAGE_KEY}.FORM.mobileTriggerPositionX`}
              onEvent={onDropdownChange}
            >
              {getXPositionOptions(t)}
            </FormComboBox>

            <FormComboBox
              control={form.control}
              name="mobileTriggerPositionY"
              label={`${INSTALLATION_FORM_PAGE_KEY}.FORM.mobileTriggerPositionY`}
              onEvent={onDropdownChange}
            >
              {getYPositionOptions(t)}
            </FormComboBox>

            <FormComboBox
              control={form.control}
              name="mobileTriggerSize"
              label={`${INSTALLATION_FORM_PAGE_KEY}.FORM.mobileTriggerSize`}
              onEvent={onDropdownChange}
            >
              {getSizeOptions(t)}
            </FormComboBox>

            <FormComboBox
              control={form.control}
              name="mobileTriggerRadius"
              label={`${INSTALLATION_FORM_PAGE_KEY}.FORM.mobileTriggerRadius`}
              onEvent={onDropdownChange}
            >
              {getShapeOptions(t)}
            </FormComboBox>

            <FormInput
              type="number"
              onInput={handleNumericInput}
              {...form.hookup(
                'mobileTriggerOffsetX',
                `${INSTALLATION_FORM_PAGE_KEY}.FORM.`,
              )}
            />
            <FormInput
              type="number"
              onInput={handleNumericInput}
              {...form.hookup(
                'mobileTriggerOffsetY',
                `${INSTALLATION_FORM_PAGE_KEY}.FORM.`,
              )}
            />
          </Grid>
        </FormSection>

        <FormSection
          label={t(`${INSTALLATION_FORM_PAGE_KEY}.WEBSITE_HELP_DETAILS.TITLE`)}
          newTag
          rightSide={
            <Flex align={'flex-end'} stretch>
              <Button
                aria-label={`${t('INFORMATION')}`}
                onClick={triggerLearnFeatureModal({})}
                scale={'primary'}
                type={'button'}
                variant="link"
              >
                <StrokeIcon name="InfoMarkWithCircle" />
                <Content
                  emphasis={'low'}
                  inline
                  scale={'primary'}
                  variant="element"
                >
                  {t(
                    `${INSTALLATION_FORM_PAGE_KEY}.WEBSITE_HELP_DETAILS.HOW_IT_WORKS`,
                  )}
                </Content>
              </Button>
            </Flex>
          }
        >
          <Flex
            align="baseline"
            gap={6}
            justify="space-between"
            paddingBlockEnd={6}
            width={'100%'}
          >
            <Content>
              {t(
                `${INSTALLATION_FORM_PAGE_KEY}.WEBSITE_HELP_DETAILS.DESCRIPTION`,
              )}
            </Content>

            <Flex gap={4} width={'100%'}>
              <Flex gap={4} width={'100%'}>
                {consistentHelpData.map((field, index) => (
                  <Grid
                    align="center"
                    cols="1fr 3fr auto"
                    gap={3}
                    justify="space-between"
                    key={field.id}
                    {...p.uptoPhablet({ cols: '1fr 1fr auto' })}
                  >
                    <FormComboBox
                      control={form.control}
                      name={`consistentHelpData.${index}.title`}
                      onEvent={onDropdownChange}
                      placeholder={t(
                        `${INSTALLATION_FORM_PAGE_KEY}.FORM.SELECT_TITLE`,
                      )}
                    >
                      {getConsistentHelpOptions(t)}
                    </FormComboBox>
                    <Tooltip
                      content={
                        !form.watch(`consistentHelpData.${index}.title`)
                          ? t(
                              `${INSTALLATION_FORM_PAGE_KEY}.FORM.SELECT_HELP_DESCRIPTION_TOOLTIP`,
                            )
                          : undefined
                      }
                      ignoreDisabled
                      scale="invert"
                      side="top"
                      zIndex={10}
                    >
                      <FormInput
                        disabled={
                          !form.watch(`consistentHelpData.${index}.title`)
                        }
                        label={null}
                        {...form.register(
                          `consistentHelpData.${index}.description`,
                        )}
                        placeholder={(() => {
                          const selectedTitle = form.watch(
                            `consistentHelpData.${index}.title`,
                          );
                          const placeholderKey = selectedTitle
                            ? HELP_TYPE_PLACEHOLDER_MAP[selectedTitle]
                            : null;
                          return placeholderKey
                            ? t(
                                `${INSTALLATION_FORM_PAGE_KEY}.FORM.${placeholderKey}`,
                              )
                            : t(
                                `${INSTALLATION_FORM_PAGE_KEY}.FORM.SELECT_HELP_DESCRIPTION_PLACEHOLDER`,
                              );
                        })()}
                        type="text"
                      />
                    </Tooltip>
                    <Button
                      aria-label={t(
                        `${INSTALLATION_FORM_PAGE_KEY}.WEBSITE_HELP_DETAILS.REMOVE_ROW_TOOLTIP`,
                      )}
                      data-mixpanel="remove-help-detail-row"
                      disabled={consistentHelpData.length === 1}
                      onClick={() => removeConsistentHelpField(index)}
                      tooltip={{
                        content: t(
                          `${INSTALLATION_FORM_PAGE_KEY}.WEBSITE_HELP_DETAILS.REMOVE_ROW_TOOLTIP`,
                        ),
                        zIndex: 10,
                        ignoreDisabled: true,
                        scale: 'invert',
                        side: 'top',
                      }}
                      type="button"
                      variant="tertiary"
                    >
                      <StrokeIcon name="Trash" />
                    </Button>
                  </Grid>
                ))}
              </Flex>

              {consistentHelpData.length < MAX_CONSISTENT_HELP_INPUTS && (
                <Flex align={'flex-start'}>
                  <Button
                    fullWidth={false}
                    onClick={addConsistentHelpField}
                    scale={'base'}
                    type="button"
                    variant={'tertiary'}
                  >
                    <StrokeIcon name="Plus" />
                    {t(
                      `${INSTALLATION_FORM_PAGE_KEY}.WEBSITE_HELP_DETAILS.ADD`,
                    )}
                  </Button>
                </Flex>
              )}

              <Content inline>
                <Trans
                  components={[
                    <Content emphasis={'low'} inline scale={'accent'} />,
                  ]}
                  i18nKey={`${INSTALLATION_FORM_PAGE_KEY}.WEBSITE_HELP_DETAILS.NOTE`}
                />
              </Content>
            </Flex>
          </Flex>
        </FormSection>

        {children}
      </Flex>
    </form>
  );
}

const WidgetsIcons = styled(Flex)`
  label {
    width: 120px;

    @media ${fromTheme('viewport.uptoMobile')} {
      width: 100%;
    }
  }
`;

function getLanguagesOptions(t: (key: string) => string) {
  return languages.map((value, idx) => (
    <Option key={value.code + idx} value={value.code} label={t(value.label)} />
  ));
}

function getXPositionOptions(t: (key: string) => string) {
  return (['left', 'right'] as string[]).map((value) => (
    <Option
      key={value}
      value={value}
      label={t(`${INSTALLATION_FORM_PAGE_KEY}.FORM.OPTIONS.${value}`)}
    />
  ));
}

function getYPositionOptions(t: (key: string) => string) {
  return (['top', 'center', 'bottom'] as string[]).map((value) => (
    <Option
      key={value}
      value={value}
      label={t(`${INSTALLATION_FORM_PAGE_KEY}.FORM.OPTIONS.${value}`)}
    />
  ));
}

function getShapeOptions(t: (key: string) => string) {
  return (['50%', '0', '10px', '5px'] as string[]).map((value) => (
    <Option
      key={value}
      value={value}
      label={t(`${INSTALLATION_FORM_PAGE_KEY}.FORM.OPTIONS.${value}`)}
    />
  ));
}

function getSizeOptions(t: (key: string) => string) {
  return (['small', 'medium', 'big'] as string[]).map((value) => (
    <Option
      key={value}
      value={value}
      label={t(`${INSTALLATION_FORM_PAGE_KEY}.FORM.OPTIONS.${value}`)}
    />
  ));
}

function getConsistentHelpOptions(t: (key: string) => string) {
  return (
    [
      'Support Page',
      'FAQ Page',
      'Contact Email',
      'Contact Phone',
      'Operation Hours',
      'Contact Page',
      'Chat Support',
    ] as string[]
  ).map((value) => (
    <Option
      key={value}
      label={t(`${INSTALLATION_FORM_PAGE_KEY}.FORM.OPTIONS.${value}`)}
      value={value}
    />
  ));
}

function getHideElementOptions(t: (key: string) => string) {
  return (['false', 'true'] as string[]).map((value) => (
    <Option
      key={value}
      value={value}
      label={t(`${INSTALLATION_FORM_PAGE_KEY}.FORM.OPTIONS.${value}`)}
    />
  ));
}

function useHasContrast(
  color: Hex,
  { r, g, b }: { r: number; g: number; b: number },
): boolean {
  return useMemo(() => {
    return wcagContrastChecker(color, [r, g, b]).uiComponent.aa;
  }, [color, r, g, b]);
}
